<template>
  <div
    v-if="item && item.attributes"
    class="group bg-white rounded p-base-1/2 gap-x-6 shadow-none hover:shadow-xl transition-shadow relative flex flex-col sm:flex-row items-start relative"
  >
    <div
      :id="`anchor-${item.attributes.slug}`"
      class="absolute top-[-5.7rem] pointer-events-none"
    ></div>

    <!-- Logo -->
    <NuxtPicture
      v-if="
        item.attributes.company &&
        item.attributes.company.data &&
        item.attributes.company.data.attributes.logo &&
        item.attributes.company.data.attributes.logo.data
      "
      :provider="
        item.attributes.company.data.attributes.logo.data.attributes.provider
      "
      :src="item.attributes.company.data.attributes.logo.data.attributes.hash"
      :width="
        item.attributes.company.data.attributes.logo.data.attributes.width
      "
      :height="
        item.attributes.company.data.attributes.logo.data.attributes.height
      "
      :sizes="'xs:100vw sm:100vw md:50vw lg:33vw xl:33vw xxl:33vw'"
      :preload="false"
      :loading="'lazy'"
      :alt="
        item.attributes.company.data.attributes.logo.data.attributes
          .alternativeText
      "
      class="w-20 hidden sm:block flex-none aspect-tv"
      :img-attrs="{
        class: 'object-contain h-full w-full transition-transform',
      }"
    />
    <NuxtPicture
      v-else-if="
        region &&
        region.attributes &&
        region.attributes.fallbackLogo &&
        region.attributes.fallbackLogo.data
      "
      :provider="region.attributes.fallbackLogo.data.attributes.provider"
      :src="region.attributes.fallbackLogo.data.attributes.hash"
      :width="region.attributes.fallbackLogo.data.attributes.width"
      :height="region.attributes.fallbackLogo.data.attributes.height"
      :sizes="'xs:100vw sm:100vw md:50vw lg:33vw xl:33vw xxl:33vw'"
      :preload="false"
      :loading="'lazy'"
      :alt="region.attributes.fallbackLogo.data.attributes.alternativeText"
      class="w-20 hidden sm:block flex-none aspect-tv"
      :img-attrs="{
        class: 'object-contain h-full w-full transition-transform',
      }"
    />
    <div v-else class="w-20 hidden sm:block flex-none aspect-tv"></div>

    <!-- Body -->
    <div class="w-full">
      <h3
        v-if="item.attributes.title"
        class="text-lg !mb-4 lg:!mb-2 text-primary group-hover:underline"
      >
        {{ item.attributes.title }}
      </h3>

      <!-- Company -->
      <div class="flex items-center gap-4 flex-none mb-5 lg:mb-3 w-full">
        <!-- Logo: Mobile -->
        <NuxtPicture
          v-if="
            item.attributes.company &&
            item.attributes.company.data &&
            item.attributes.company.data.attributes.logo &&
            item.attributes.company.data.attributes.logo.data
          "
          :provider="
            item.attributes.company.data.attributes.logo.data.attributes
              .provider
          "
          :src="
            item.attributes.company.data.attributes.logo.data.attributes.hash
          "
          :width="
            item.attributes.company.data.attributes.logo.data.attributes.width
          "
          :height="
            item.attributes.company.data.attributes.logo.data.attributes.height
          "
          :sizes="'xs:100vw sm:100vw md:50vw lg:33vw xl:33vw xxl:33vw'"
          :preload="false"
          :loading="'lazy'"
          :alt="
            item.attributes.company.data.attributes.logo.data.attributes
              .alternativeText
          "
          class="w-20 flex-none aspect-tv sm:hidden"
          :img-attrs="{
            class: 'object-contain h-full w-full transition-transform',
          }"
        />
        <div class="leading-none">
          <div
            v-if="item.attributes.company.data"
            class="font-bold leading-none break-words lg:[hyphens:auto]"
          >
            {{ item.attributes.company.data.attributes.title }}
          </div>
          <div v-if="item.attributes.places.data.length" class="text-sm">
            <template
              v-for="(place, index) in item.attributes.places.data"
              :key="`${item.id}-place-${place.id}`"
            >
              {{
                place.attributes.title +
                (index < item.attributes.places.data.length - 1 ? ", " : "")
              }}
            </template>
          </div>
        </div>
      </div>

      <!-- Labels -->
      <div class="flex w-full overflow-hidden relative">
        <!-- after:bg-gradient-to-r after:absolute after:h-8 after:w-12 after:right-0 after:bottom-0 after:from-transparent after:to-white -->
        <VacancyLabels
          :extra-classes="'mt-2 mt-auto'"
          :hours-from="item.attributes.hoursFrom"
          :hours-to="item.attributes.hoursTo"
          :educations="item.attributes.educations.data"
          :monthly-salary-from="item.attributes.monthlySalaryFrom"
          :monthly-salary-to="item.attributes.monthlySalaryTo"
          :hourly-salary-from="item.attributes.hourlySalaryFrom"
          :hourly-salary-to="item.attributes.hourlySalaryTo"
          :salary-description="item.attributes.salaryDescription"
        />
      </div>
    </div>

    <!-- Buttons -->
    <div
      class="flex flex-row-reverse w-full sm:w-auto mt-4 sm:mt-0 sm:flex-col gap-x-4 items-center flex-none"
    >
      <NuxtLink
        class="btn text-nowrap relative z-10"
        :to="
          localePath({
            name: `${collection}-slug`,
            params: { slug: `${item.attributes.slug}` },
            hash: '#solliciteer',
          })
        "
        :prefetch="true"
        :prefetched-class="'prefetched'"
        >{{ $t("general.apply") }}</NuxtLink
      >
      <NuxtLink
        class="btn btn-link after:absolute after:inset-0"
        :to="
          localePath({
            name: `${collection}-slug`,
            params: { slug: `${item.attributes.slug}` },
          })
        "
        :prefetch="true"
        :prefetched-class="'prefetched'"
        >{{ $t("general.viewVacancy") }}</NuxtLink
      >
    </div>
  </div>
</template>

<script setup lang="ts">
import type { StrapiPage } from "@flyingkiwi/nuxt-components";
import type { Strapi4ResponseData } from "@nuxtjs/strapi";
const { region } = useStaticData();
const localePath = useLocalePath();
const { collectionKey } = useInjectionKeys();
const collection = inject(collectionKey);
defineProps<{
  item: Strapi4ResponseData<StrapiPage>;
  idx: number;
}>();
</script>
